import React, { useRef, useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { X, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { resolveImageUrl } from '../lib/imageUtils';
import ImageEditorModal from './ImageEditorModal';

export const ImageItem = React.memo(({
  image,
  updateImage,
  deleteImage,
  onItemClick,
  isConnecting,
  isSelected,
  zoom = 1,
  shouldDeleteOnDrop,
  onMultiDrag,
  selectedCount = 0,
  onDragStart,
  onDragEnd,
}) => {
  const nodeRef = useRef(null);
  const lastDragPosRef = useRef({ x: 0, y: 0 });
  const [displayUrl, setDisplayUrl] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const rawSrc = image.url ?? image.data;

  useEffect(() => {
    let active = true;
    let objectUrlToRevoke = '';

    async function loadSrc() {
      const resolved = await resolveImageUrl(rawSrc);
      if (active) {
        setDisplayUrl(resolved);
        if (resolved?.startsWith('blob:')) {
          objectUrlToRevoke = resolved;
        }
      }
    }

    loadSrc();

    return () => {
      active = false;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [rawSrc]);

  const handleResize = (e) => {
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = image.size?.width || 300;
    const startHeight = image.size?.height || 200;

    const handleMouseMove = (moveEvent) => {
      const deltaX = (moveEvent.clientX - startX) / zoom;
      const deltaY = (moveEvent.clientY - startY) / zoom;

      updateImage(image.id, {
        size: {
          width: Math.max(100, startWidth + deltaX),
          height: Math.max(80, startHeight + deltaY),
        }
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleConnectionClick = (e) => {
    if (isConnecting) {
      e.stopPropagation();
      onItemClick?.();
    }
  };

  const handleDoubleClick = (e) => {
    if (isConnecting) return;
    e.stopPropagation();
    setEditorOpen(true);
  };

  const handleEditorSave = ({ dataUrl, width, height }) => {
    const currentWidth = image.size?.width || 300;
    const newHeight = Math.round(currentWidth * (height / width)) || currentWidth;
    updateImage(image.id, {
      data: dataUrl,
      url: null,
      size: { width: currentWidth, height: newHeight },
    });
    setEditorOpen(false);
    toast.success('Image updated');
  };

  return (
    <>
      <Draggable
        nodeRef={nodeRef}
      position={image.position}
      scale={zoom}
      disabled={isConnecting}
      cancel="button, [data-no-drag]"
      onStart={(e, data) => {
        lastDragPosRef.current = { x: data.x, y: data.y };
        onDragStart?.();
      }}
      onDrag={(e, data) => {
        if (isSelected && selectedCount > 1 && onMultiDrag) {
          const deltaX = data.x - lastDragPosRef.current.x;
          const deltaY = data.y - lastDragPosRef.current.y;
          onMultiDrag(deltaX, deltaY);
        }
        lastDragPosRef.current = { x: data.x, y: data.y };
      }}
      onStop={(e, data) => {
        onDragEnd?.();
        if (shouldDeleteOnDrop && shouldDeleteOnDrop(e)) {
          deleteImage(image.id);
        } else {
          updateImage(image.id, {
            position: { x: data.x, y: data.y },
          });
        }
      }}
    >
      <div
        ref={nodeRef}
        onClick={handleConnectionClick}
        onDoubleClick={handleDoubleClick}
        className={`absolute group ${
          isConnecting
            ? 'cursor-pointer hover:ring-2 hover:ring-primary'
            : 'cursor-move'
        } ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
        style={{
          width: image.size?.width || 300,
          height: image.size?.height || 200,
          zIndex: 12
        }}
      >
        <div className="relative w-full h-full">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Canvas image"
              className="w-full h-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-slate-800 animate-pulse" />
          )}

          {/* Delete Button */}
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              deleteImage(image.id);
            }}
          >
            <X className="h-3 w-3" />
          </Button>

          {/* Resize Handle (subtle) */}
          <div
            data-no-drag
            className="absolute bottom-1 right-1 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity flex items-center justify-center"
            onMouseDown={handleResize}
          >
            <Maximize2 className="h-3 w-3 text-muted-foreground/70 rotate-90" />
          </div>
        </div>
      </div>
      </Draggable>
      <ImageEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        imageUrl={displayUrl}
        onSave={handleEditorSave}
      />
    </>
  );
});

ImageItem.displayName = 'ImageItem';
export default ImageItem;

